import { getConstraintDisplayName } from "../../../utils/constraintNames";
import { stripNamespacePrefix as stripPrefix } from "../../../utils/nameUtils";

export const SORT_FALLBACK_OFFSET = 1_000_000;

const getWorldBodyUuid = (worldBody) =>
  typeof worldBody === "string" ? worldBody : worldBody?.uuid;

export const compareTargets = (a, b) => {
  const orderDelta = (a.sortIndex ?? SORT_FALLBACK_OFFSET) - (b.sortIndex ?? SORT_FALLBACK_OFFSET);
  if (orderDelta !== 0) return orderDelta;
  return String(a.label || "").localeCompare(String(b.label || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const geomLabel = ({ body, geom }) => {
  const name = stripPrefix(geom?.name) || geom?.kind || "geom";
  const bodyName = stripPrefix(body?.name) || body?.uuid || "body";
  return `${name} in ${bodyName}`;
};

export const buildSceneOrder = (bodies, worldBodies, tendons) => {
  const orderedBodyUuids = [];
  const bodyOrder = new Map();
  const itemOrder = new Map();
  const visited = new Set();
  let order = 0;

  const addItemOrder = (keys) => {
    keys.filter(Boolean).forEach((key) => {
      if (!itemOrder.has(key)) itemOrder.set(key, order);
    });
    order += 1;
  };

  const visitBody = (uuid) => {
    if (!uuid || visited.has(uuid)) return;
    const body = bodies?.[uuid];
    if (!body) return;

    visited.add(uuid);
    bodyOrder.set(uuid, orderedBodyUuids.length);
    orderedBodyUuids.push(uuid);
    addItemOrder([`body:${uuid}`, uuid]);

    if (body.inertia?.uuid) {
      addItemOrder([`inertial:${uuid}:${body.inertia.uuid}`, body.inertia.uuid]);
    }

    (body.joints || []).forEach((jointUuid) => addItemOrder([`joint:${jointUuid}`, jointUuid]));
    (body.sites || []).forEach((siteUuid) => addItemOrder([`site:${siteUuid}`, siteUuid]));
    (body.geoms || []).forEach((geom) => {
      if (!geom?.uuid) return;
      addItemOrder([`geom:${uuid}:${geom.uuid}`, `${uuid}:${geom.uuid}`, geom.uuid]);
    });
    (body.children || []).forEach(visitBody);
  };

  (worldBodies || []).forEach((worldBody) => visitBody(getWorldBodyUuid(worldBody)));

  Object.values(bodies || {})
    .filter((body) => body?.uuid && !visited.has(body.uuid))
    .sort((a, b) =>
      compareTargets(
        {
          label: stripPrefix(a.name) || a.uuid,
        },
        {
          label: stripPrefix(b.name) || b.uuid,
        }
      )
    )
    .forEach((body) => visitBody(body.uuid));

  Object.values(tendons || {}).forEach((tendon) => {
    if (!tendon?.uuid) return;
    const tendonOrder = (tendon.joints || []).reduce((best, jointRef) => {
      const jointOrder = itemOrder.get(`joint:${jointRef?.joint}`);
      return jointOrder == null ? best : Math.min(best, jointOrder);
    }, SORT_FALLBACK_OFFSET);
    itemOrder.set(`tendon:${tendon.uuid}`, tendonOrder);
    itemOrder.set(tendon.uuid, tendonOrder);
  });

  return { orderedBodyUuids, bodyOrder, itemOrder };
};

export const getTargetSortIndex = (sceneOrder, ...keys) => {
  for (const key of keys) {
    if (!key) continue;
    const sortIndex = sceneOrder.itemOrder.get(key);
    if (sortIndex != null) return sortIndex;
  }
  return SORT_FALLBACK_OFFSET;
};

export const buildBodyTargets = (bodies, sceneOrder) =>
  sceneOrder.orderedBodyUuids
    .map((bodyUuid) => bodies?.[bodyUuid])
    .filter((body) => body?.uuid)
    .map((body) => ({
      id: body.uuid,
      label: stripPrefix(body.name) || body.uuid,
      detail: "Body",
      sortIndex: sceneOrder.bodyOrder.get(body.uuid) ?? SORT_FALLBACK_OFFSET,
    }));

export const buildObjectTargets = (bodies, worldBodies, sceneOrder) =>
  (worldBodies || [])
    .map((worldBody) => {
      const uuid = getWorldBodyUuid(worldBody);
      const body = uuid ? bodies?.[uuid] : null;
      if (!uuid || !body) return null;
      return {
        id: uuid,
        label:
          stripPrefix(typeof worldBody === "object" ? worldBody.name || body.name : body.name) ||
          uuid,
        detail: "Root object",
        sortIndex: sceneOrder.bodyOrder.get(uuid) ?? SORT_FALLBACK_OFFSET,
      };
    })
    .filter(Boolean)
    .sort(compareTargets);

export const buildGeomTargets = (bodies, sceneOrder, visualOnly) =>
  sceneOrder.orderedBodyUuids
    .flatMap((bodyUuid) => {
      const body = bodies?.[bodyUuid];
      return (body?.geoms || [])
        .filter((geom) => geom?.uuid && geom.visualOnly === visualOnly)
        .map((geom) => ({
          id: `${body.uuid}:${geom.uuid}`,
          label: geomLabel({ body, geom }),
          detail: geom?.kind || (visualOnly ? "Visual" : "Collision"),
          sortIndex: getTargetSortIndex(
            sceneOrder,
            `geom:${body.uuid}:${geom.uuid}`,
            `${body.uuid}:${geom.uuid}`
          ),
        }));
    })
    .sort(compareTargets);

export const buildInertialTargets = (bodies, sceneOrder) =>
  sceneOrder.orderedBodyUuids
    .map((bodyUuid) => bodies?.[bodyUuid])
    .filter((body) => body?.uuid && body?.inertia?.uuid)
    .map((body) => ({
      id: `${body.uuid}:${body.inertia.uuid}`,
      label: stripPrefix(body.inertia.name) || stripPrefix(body.name) || body.uuid,
      detail: stripPrefix(body.name) || body.uuid,
      sortIndex: getTargetSortIndex(sceneOrder, `inertial:${body.uuid}:${body.inertia.uuid}`),
    }));

export const buildMeshTargets = (bodies, meshes, sceneOrder) => {
  const meshSortIndex = new Map();
  sceneOrder.orderedBodyUuids.forEach((bodyUuid) => {
    const body = bodies?.[bodyUuid];
    (body?.geoms || []).forEach((geom) => {
      if (!geom?.mesh) return;
      const sortIndex = getTargetSortIndex(
        sceneOrder,
        `geom:${body.uuid}:${geom.uuid}`,
        `${body.uuid}:${geom.uuid}`
      );
      meshSortIndex.set(geom.mesh, Math.min(meshSortIndex.get(geom.mesh) ?? sortIndex, sortIndex));
    });
  });
  return [...meshSortIndex.keys()]
    .map((meshUuid) => {
      const mesh = meshes?.[meshUuid];
      if (!mesh?.uuid) return null;
      return {
        id: mesh.uuid,
        label: stripPrefix(mesh.name) || mesh.uuid,
        detail: "Referenced mesh",
        sortIndex: meshSortIndex.get(meshUuid) ?? SORT_FALLBACK_OFFSET,
      };
    })
    .filter(Boolean)
    .sort(compareTargets);
};

export const buildRecordTargets = (record, fallbackLabel, sceneOrder) =>
  Object.values(record || {})
    .filter((entry) => entry?.uuid)
    .map((entry) => ({
      id: entry.uuid,
      label: stripPrefix(entry.name) || entry.kind || fallbackLabel || entry.uuid,
      detail: entry.kind || fallbackLabel || "",
      sortIndex: getTargetSortIndex(
        sceneOrder,
        entry.uuid,
        `${fallbackLabel?.toLowerCase()}:${entry.uuid}`
      ),
    }))
    .sort(compareTargets);

export const buildJointTargets = (joints, sceneOrder) =>
  buildRecordTargets(joints, "Joint", sceneOrder).filter((target) => {
    const joint = joints?.[target.id];
    return joint?.kind !== "free";
  });

export const buildActuatorTargets = (actuators, sceneOrder) =>
  Object.values(actuators || {})
    .filter((actuator) => actuator?.uuid)
    .map((actuator) => ({
      id: actuator.uuid,
      label: stripPrefix(actuator.name) || actuator.kind || "Actuator",
      detail: actuator.kind || "Actuator",
      sortIndex: Math.min(
        getTargetSortIndex(sceneOrder, `joint:${actuator.joint}`, actuator.joint),
        getTargetSortIndex(sceneOrder, `tendon:${actuator.tendonUuid}`, actuator.tendonUuid)
      ),
    }))
    .sort(compareTargets);

export const buildEqualityTargets = (equalities, sceneOrder) =>
  Object.values(equalities || {})
    .filter((equality) => equality?.uuid)
    .map((equality) => ({
      id: equality.uuid,
      label: getConstraintDisplayName(equality, equalities),
      detail: equality.type || "Constraint",
      sortIndex: Math.min(
        getTargetSortIndex(
          sceneOrder,
          equality.obj1,
          `body:${equality.obj1}`,
          `joint:${equality.obj1}`,
          `site:${equality.obj1}`,
          `tendon:${equality.obj1}`
        ),
        getTargetSortIndex(
          sceneOrder,
          equality.obj2,
          `body:${equality.obj2}`,
          `joint:${equality.obj2}`,
          `site:${equality.obj2}`,
          `tendon:${equality.obj2}`
        )
      ),
    }))
    .sort(compareTargets);

export const buildCameraTargets = (cameras, sceneOrder) =>
  Object.values(cameras || {})
    .filter((camera) => camera?.uuid)
    .map((camera) => ({
      id: camera.uuid,
      label: stripPrefix(camera.name) || camera.kind || "Camera",
      detail: camera.kind || "Camera",
      sortIndex: Math.min(
        getTargetSortIndex(sceneOrder, `body:${camera.mountBodyUuid}`, camera.mountBodyUuid),
        getTargetSortIndex(sceneOrder, `site:${camera.mountSiteUuid}`, camera.mountSiteUuid)
      ),
    }))
    .sort(compareTargets);
